import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import ResumeForm from './ResumeForm';
import ResumePreview from './ResumePreview';
import { exportToPDF } from '../../utils/pdfExport';
import { FaSave, FaFileDownload } from 'react-icons/fa';
import { useDocumentSave } from '../../hooks/useDocumentSave';
import { useAuth } from '../../hooks/useAuth';

const ResumeBuilder = () => {
  const { currentUser } = useAuth();
  const { saveDocument } = useDocumentSave();
  const [resumeData, setResumeData] = useState({
    personalInfo: {
      fullName: '',
      email: '',
      phone: '',
      location: '',
      linkedIn: '',
      portfolio: '',
    },
    workExperience: [],
    education: [],
    skills: [],
    projects: []
  });

  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleUpdateData = (section, data) => {
    setResumeData(prev => ({
      ...prev,
      [section]: data
    }));
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      const loadingToast = toast.loading('Exporting your resume...');

      // Check if required fields are filled
      if (!resumeData.personalInfo.fullName) {
        toast.dismiss(loadingToast);
        toast.error('Please enter your name before exporting');
        return;
      }

      // Wait for the preview to update
      await new Promise(resolve => setTimeout(resolve, 100));

      // Try to export
      await exportToPDF(
        'resume-preview',
        `${resumeData.personalInfo.fullName || 'Resume'}-${new Date().toISOString().split('T')[0]}.pdf`
      );

      toast.dismiss(loadingToast);
      toast.success('Resume exported successfully!');
    } catch (error) {
      console.error('Error exporting resume:', error);
      toast.error(error.message || 'Failed to export resume. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleSaveResume = async () => {
    if (!currentUser) {
      toast.error('Please sign in to save your resume');
      return;
    }

    try {
      setSaving(true);
      const title = resumeData.personalInfo.fullName || 'Untitled Resume';
      await saveDocument('resume', resumeData, title);
      toast.success('Resume saved successfully!');
    } catch (error) {
      console.error('Error saving resume:', error);
      toast.error('Failed to save resume. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-4xl font-bold text-white bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
              Create Your Resume
            </h1>
            <div className="flex space-x-4">
              <button
                onClick={handleSaveResume}
                disabled={saving}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold 
                         hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:-translate-y-1 
                         hover:shadow-lg hover:shadow-blue-500/25 flex items-center gap-2
                         disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none"
              >
                <FaSave className={`w-5 h-5 ${saving ? 'animate-spin' : ''}`} />
                {saving ? 'Saving...' : 'Save Resume'}
              </button>
              <button
                onClick={handleExportPDF}
                disabled={exporting}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-semibold 
                         hover:from-purple-700 hover:to-purple-800 transition-all duration-300 transform hover:-translate-y-1 
                         hover:shadow-lg hover:shadow-purple-500/25 flex items-center gap-2 disabled:opacity-50 
                         disabled:hover:transform-none disabled:hover:shadow-none"
              >
                <FaFileDownload className="w-5 h-5" />
                {exporting ? 'Exporting...' : 'Export PDF'}
              </button>
            </div>
          </div>
          
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="w-full lg:w-1/2">
              <div className="bg-[#1A1A1A] shadow-xl rounded-2xl p-6 border border-gray-800
                            backdrop-blur-xl bg-opacity-80">
                <ResumeForm 
                  data={resumeData} 
                  onUpdateData={handleUpdateData}
                />
              </div>
            </div>
            
            <div className="w-full lg:w-1/2">
              <div className="sticky top-6">
                <div className="bg-[#1A1A1A] shadow-xl rounded-2xl p-6 border border-gray-800
                              backdrop-blur-xl bg-opacity-80">
                  <div id="resume-preview" className="bg-white rounded-lg overflow-hidden">
                    <ResumePreview 
                      resumeData={resumeData}
                      template={selectedTemplate}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeBuilder;
